import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AuthState = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  ready: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState>({
  user: null,
  session: null,
  loading: true,
  ready: false,
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isActive = true;
    let hasAuthEvent = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!isActive) return;
      hasAuthEvent = true;
      setSession(s);
      setLoading(false);
    });
    supabase.auth.getSession().then(({ data }) => {
      if (!isActive || hasAuthEvent) return;
      setSession(data.session);
      setLoading(false);
    });

    // "Zapomni si me" brez odjave med Google OAuth preusmeritvijo. sessionStorage
    // preživi preusmeritev v istem zavihku, ob zaprtju zavihka pa se izbriše.
    try {
      const shouldRemember = window.localStorage.getItem("kupish:remember-me") !== "false";
      const isCurrentTabSession = window.sessionStorage.getItem("kupish:session-active") === "true";
      if (!shouldRemember && !isCurrentTabSession) {
        void supabase.auth.signOut({ scope: "local" });
      }
    } catch {}

    return () => {
      isActive = false;
      subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: session?.user ?? null,
        session,
        loading,
        ready: !loading,
        signOut: async () => {
          await supabase.auth.signOut();
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
